/* ============================================================
   GGC Planners — Add-to-Cart fix  (v3)
   ------------------------------------------------------------
   Combines fixes for both Qikify-on and Qikify-off modes.

   Root causes addressed:

   (A) The Sense theme's <product-form> element calls
       `this.cartNotification.setActiveElement(...)` in its submit
       handler. On this storefront `<cart-notification>` is not
       present, so `cartNotification` is null and the handler
       throws silently. Worse, the handler is `bind()`'d in the
       constructor, so patching the prototype after the fact
       doesn't help. We work around this by attaching our own
       submit listener in the CAPTURE phase that runs before the
       theme's broken bubble-phase listener and uses
       `stopImmediatePropagation()` to suppress it, then performs
       the cart/add request ourselves.

   (B) Qikify Slide Cart's full-viewport `.qsc2-overlay` ships
       with `pointer-events: auto`. Even when the drawer is
       closed (visibility: hidden, z-index: -1), the overlay
       silently captures every click in the viewport — so even
       clicks at the button's coordinates never reach it. Two
       lines of CSS flip the overlay back to `pointer-events:
       none` unless the drawer is genuinely open.

   After our submit interceptor fires the cart/add request:
     - If Qikify is enabled, its fetch interceptor catches the
       request and opens its drawer. Nothing else to do.
     - If Qikify is disabled, no drawer/notification appears, so
       we navigate the customer to /cart instead.

   Reversible: delete the asset file + remove the <script> tag
   from theme.liquid and the storefront is back to its prior
   state.
   ============================================================ */
(function () {
  'use strict';

  // -----------------------------------------------------------
  // (B) CSS fix for the Qikify overlay click-blocker.
  // -----------------------------------------------------------
  function injectOverlayCssFix() {
    if (document.getElementById('ggc-add-to-cart-fix-style')) return;
    var style = document.createElement('style');
    style.id = 'ggc-add-to-cart-fix-style';
    style.textContent =
      '.qsc2-overlay { pointer-events: none !important; }\n' +
      'body.qsc2-drawer-opened .qsc2-overlay { pointer-events: auto !important; }';
    (document.head || document.documentElement).appendChild(style);
  }
  injectOverlayCssFix();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', injectOverlayCssFix);
  }

  // -----------------------------------------------------------
  // (A) Capture-phase submit interceptor that replaces the
  //     theme's broken handler.
  // -----------------------------------------------------------
  // Any drawer/notification UI a connected app might be opening.
  function anyCartUiOpen() {
    var b = document.body;
    if (!b) return false;
    if (b.classList.contains('qsc2-drawer-opened')) return true;       // Qikify
    if (b.classList.contains('cart-drawer-opened')) return true;       // common Dawn fork
    if (b.classList.contains('drawer-opened')) return true;            // generic
    if (b.classList.contains('overflow-hidden')) return true;          // many modals
    var live = document.querySelector(
      'cart-drawer:not([hidden]):not(.is-empty), ' +
      'cart-notification:not([hidden]):not(.is-empty), ' +
      '.cart-drawer.active, .cart-notification.active, ' +
      '.qsc2-cart-drawer.active'
    );
    return !!live;
  }

  function showError(productForm, message) {
    var errWrap = productForm.querySelector('.product-form__error-message-wrapper');
    var errMsg  = productForm.querySelector('.product-form__error-message');
    if (errWrap && errMsg) {
      errWrap.removeAttribute('hidden');
      errMsg.textContent = message || 'Could not add to cart.';
    } else {
      alert(message || 'Could not add to cart.');
    }
  }

  function setLoading(productForm, on) {
    var btn = productForm.querySelector('[type="submit"]');
    var spinner = productForm.querySelector('.loading-overlay__spinner');
    if (btn) {
      if (on) {
        btn.classList.add('loading');
        btn.setAttribute('aria-disabled', 'true');
      } else {
        btn.classList.remove('loading');
        btn.removeAttribute('aria-disabled');
      }
    }
    if (spinner) {
      if (on) spinner.classList.remove('hidden');
      else    spinner.classList.add('hidden');
    }
  }

  function handleSubmit(evt) {
    var form = evt.currentTarget;
    var productForm = form.closest('product-form');
    if (!productForm) return;

    // Suppress the theme's broken bubble-phase handler.
    evt.preventDefault();
    evt.stopImmediatePropagation();

    setLoading(productForm, true);

    // Build the request body. Don't append "sections" — we don't
    // need section rendering here, the page is going to either
    // hand off to Qikify's drawer or navigate to /cart.
    var formData = new FormData(form);
    formData.delete('sections');
    formData.delete('sections_url');

    var cartAddUrl = '/cart/add.js';
    if (window.routes && window.routes.cart_add_url) {
      cartAddUrl = window.routes.cart_add_url + '.js';
    }

    fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        'Accept': 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    })
      .then(function (r) {
        return r.json().then(function (data) { return { ok: r.ok, data: data }; });
      })
      .then(function (result) {
        if (!result.ok || (result.data && result.data.status)) {
          setLoading(productForm, false);
          showError(productForm,
            (result.data && (result.data.description || result.data.message))
            || 'Could not add to cart.');
          return;
        }

        // Cart updated. Some app (Qikify slide cart, a future
        // cart notification, etc.) may be opening a drawer or
        // notification in response to our fetch. Give it a brief
        // window to do so. If nothing visible appears, navigate
        // the customer to /cart so they see their cart.
        var start = Date.now();
        (function waitForUi() {
          if (anyCartUiOpen()) {
            setLoading(productForm, false);
            return;
          }
          if (Date.now() - start > 700) {
            window.location.href = '/cart';
            return;
          }
          setTimeout(waitForUi, 50);
        })();
      })
      .catch(function (e) {
        setLoading(productForm, false);
        showError(productForm, (e && e.message) || 'Could not add to cart.');
        console.error('[GGC] cart add failed:', e);
      });
  }

  function attachInterceptor(productForm) {
    if (!productForm || productForm.__ggcAttached) return;
    var form = productForm.querySelector('form[action*="/cart/add"]');
    if (!form) return;
    form.addEventListener('submit', handleSubmit, true); // capture phase
    productForm.__ggcAttached = true;
  }

  function attachAllInterceptors() {
    var pfs = document.querySelectorAll('product-form');
    for (var i = 0; i < pfs.length; i++) attachInterceptor(pfs[i]);
  }

  // Hook up interceptors as early as possible.
  attachAllInterceptors();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attachAllInterceptors);
  }

  // Catch product-form elements that are added later (quick-view,
  // recommendation widgets, etc.).
  if (window.MutationObserver) {
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.tagName === 'PRODUCT-FORM') attachInterceptor(node);
          var inner = node.querySelectorAll && node.querySelectorAll('product-form');
          if (inner) for (var k = 0; k < inner.length; k++) attachInterceptor(inner[k]);
        }
      }
    }).observe(document.body || document.documentElement,
              { childList: true, subtree: true });
  }

  // -----------------------------------------------------------
  // Self-heal stuck Qikify drawer-opened state (kept from v2).
  // -----------------------------------------------------------
  function isDrawerActuallyVisible() {
    var drawer = document.querySelector(
      '.qsc2-cart-drawer, .qsc2-drawer, [class*="qsc2"][class*="drawer"]:not(body)'
    );
    if (!drawer) return false;
    var r = drawer.getBoundingClientRect();
    var cs = window.getComputedStyle(drawer);
    return r.width > 50 && r.height > 50 &&
           cs.visibility !== 'hidden' &&
           cs.display !== 'none' &&
           parseFloat(cs.opacity) > 0.01;
  }
  function clearStuckDrawerState() {
    if (!document.body.classList.contains('qsc2-drawer-opened')) return;
    if (!isDrawerActuallyVisible()) {
      document.body.classList.remove('qsc2-drawer-opened');
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', clearStuckDrawerState);
  } else {
    clearStuckDrawerState();
  }
  setTimeout(clearStuckDrawerState, 500);
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' &&
        document.body.classList.contains('qsc2-drawer-opened') &&
        !isDrawerActuallyVisible()) {
      document.body.classList.remove('qsc2-drawer-opened');
    }
  });
})();